import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCanAccess } from '@permissions/useFeatures';
import { DndContext } from '@dnd-kit/core';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { PageTour } from '@components/PageTour/PageTour';
import { useDocumentsTour } from './tour/useDocumentsTour';
import { DOCUMENTS_TOUR_ID, DOCUMENTS_TOUR_STEPS } from './tour/documentsTour';
import { useDocumentsUrl } from './hooks/useDocumentsUrl';
import { useDocumentsDnd } from './hooks/useDocumentsDnd';
import { useFolders } from './hooks/useFolders';
import { useNewFolder } from './hooks/useNewFolder';
import { useFiles } from './hooks/useFiles';
import { useHeldRecord } from '@hooks/useHeldRecord';
import { useDeleteConfirmation } from './hooks/useDeleteConfirmation';
import { useVersionApproval } from './hooks/useVersionApproval';
import { FolderNav } from './FolderNav/FolderNav';
import { DocumentsBar } from './DocumentsBar/DocumentsBar';
import { useDocumentsView } from './hooks/useDocumentsView';
import { DocumentView } from './DocumentView/DocumentView';
import { folderDisplayName } from './utils/folderDisplayName';
import { canCreateSubfolder } from './utils/validation';
import { documentsErrorMessage } from './utils/errorMessages';
import { DocumentDrawer } from './DocumentDrawer/DocumentDrawer';
import { annotationsPath } from '@components/Pages/AnnotationsPage/annotations.url';
import { UploadModal } from './UploadModal/UploadModal';
import { downloadNameFor } from '@apis/documents';
import type { IDocument, IDocumentVersion, IFilesResponse } from '@apis/documents';
import styles from './DocumentsPage.module.css';

export function DocumentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toasts, push, dismiss } = useToasts();

  // --- Routing / URL-derived state ----------------------------------------
  const {
    selectedFolder, setSelectedFolder,
    selectedFileId, setSelectedFileId,
    compareVersion, setVersionParam,
    searchInput, setSearchInput, searchQuery,
    followFolderRename,
  } = useDocumentsUrl();

  const { view, setView } = useDocumentsView();

  // Writing is a feature of its own (approve/delete follow the server's
  // capability flags below): without it the write handlers are not passed.
  const canWrite = useCanAccess('documents.write');

  // --- Data ----------------------------------------------------------------
  // The URL's site, not the stored working site: a link to another site must
  // not show this one's tree while the store catches up.
  const { siteId } = useParams<{ siteId: string }>();
  const queryClient = useQueryClient();

  const {
    folders, canDelete, canMove, canApprove, isPending, isError, providerError: folderProviderError,
    renameFolder, deleteFolder, addFolder, moveFolder,
  } = useFolders(
    siteId,
    (op, error) => push({ kind: 'error', title: t(`documents.${op}FolderError`), sub: documentsErrorMessage(t, error, { sentName: op === 'rename' }) }),
    followFolderRename,
  );

  const activeFolderId = selectedFolder === 'all' ? null : selectedFolder;

  const { newFolder, start: startNewFolder, commit: commitNewFolder, cancel: cancelNewFolder } = useNewFolder({
    siteId,
    folders,
    onCreated: addFolder,
    onError: (error) =>
      push({ kind: 'error', title: t('documents.createFolderError'), sub: documentsErrorMessage(t, error, { sentName: true }) }),
  });

  const {
    documents,
    isPending: filesPending,
    isError: filesError,
    providerError: fileProviderError,
    renameDoc, deleteDoc, deleteVersion, moveDoc,
  } = useFiles(siteId, activeFolderId, (op, error) =>
    push({
      kind: 'error',
      title: op === 'deleteVersion' ? t('documents.deleteVersionError') : t(`documents.${op}FileError`),
      sub: documentsErrorMessage(t, error, { sentName: op === 'rename' }),
    }),
  );

  // Either query can surface a provider error (not connected / session expired);
  // whichever fired drives one banner, rendered provider-agnostically.
  const providerError = folderProviderError ?? fileProviderError;

  // --- Document drawer -----------------------------------------------------
  // The open drawer is the ?doc= param; the drawer slides out on its own.
  const openDoc = (id: string) => setSelectedFileId(id);
  const closeDrawer = () => setSelectedFileId(null);

  // --- Drag and drop -------------------------------------------------------
  const { sensors, collisionDetection, invalidDropId, handleDragEnd } =
    useDocumentsDnd({ folders, moveFolder, moveDoc });

  // Whether `descendantId` sits at or below `id` in the tree.
  function isSelfOrAncestor(id: string, descendantId: string): boolean {
    let current: string | null = descendantId;
    while (current !== null) {
      if (current === id) return true;
      current = folders.find((f) => f.id === current)?.parentId ?? null;
    }
    return false;
  }

  // --- Delete confirmation -------------------------------------------------
  async function performDeleteVersion(documentId: string, seq: number) {
    const result = await deleteVersion(documentId, seq);
    if (result && selectedFileId === result.deletedFileId) {
      if (result.presentedFileId) openDoc(result.presentedFileId); 
      else closeDrawer();
    }
  }

  // --- Approval ------------------------------------------------------------
  const { busyKey: busyVersionKey, submit: submitVersion, decide: decideVersion } =
    useVersionApproval(siteId, (op, error) =>
      push({ kind: 'error', title: t(`documents.approval.${op}Error`), sub: documentsErrorMessage(t, error) }),
    );

  // Approving moves the row onto the newly approved version's file, which is
  // what `?doc=` addresses — follow it, or the open drawer goes blank.
  async function performDecideVersion(
    doc: IDocument,
    version: IDocumentVersion,
    decision: 'approve' | 'reject',
    comment: string,
  ): Promise<boolean> {
    const ok = await decideVersion(doc, version.version, decision, comment);
    if (ok && decision === 'approve' && selectedFileId === doc.id) openDoc(version.fileId);
    return ok;
  }

  const { pendingDelete, isDeleting, requestDelete, confirmDelete, cancelDelete } =
    useDeleteConfirmation({
      deleteFolder,
      deleteDoc,
      deleteVersion: performDeleteVersion,
      onConfirmed: (target) => {
        if (target.type === 'folder' && activeFolderId && isSelfOrAncestor(target.id, activeFolderId)) {
          setSelectedFolder(folders.find((f) => f.id === target.id)?.parentId ?? 'all', { replace: true });
        }
        if (target.type === 'doc' && selectedFileId === target.id) closeDrawer();
      },
    });

  // --- Upload --------------------------------------------------------------
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // The document whose next version the modal should upload, when it was opened
  // from the drawer button or a row's context menu. Null = an ordinary upload
  // of a new file. Same name as the prop it feeds, so the intent survives the
  // hop into UploadModal.
  const [newVersionTarget, setNewVersionTarget] = useState<IDocument | null>(null);

  const closeUpload = useCallback(() => { setUploadModalOpen(false); setNewVersionTarget(null); }, []);

  function openNewVersionUpload(fileId: string) {
    const doc = documents.find((d) => d.id === fileId);
    if (!doc) return;
    setNewVersionTarget(doc);
    setUploadModalOpen(true);
  }

  // The table's background context menu creates inside the folder being viewed.
  // Not allowed at the virtual root (folders live inside a system root only)
  // or once the current folder sits at the depth limit.
  const canCreateInCurrentFolder =
    activeFolderId !== null && canCreateSubfolder(folders, activeFolderId);

  // Approve-on-upload runs through the normal decision rules, which refuse when
  // an older version is still waiting (decisions go oldest-first). The upload
  // still landed, so this is a notice rather than a failure — but it has to be
  // said, or the version looks published when it is only saved.
  function notifyAutoApproveSkipped() {
    push({
      kind: 'error',
      title: t('documents.upload.approveSkipped'),
      sub: t('documents.upload.approveSkippedHint'),
    });
  }

  // --- Shared by both renderers --------------------------------------------
  // The table and the grid take the same actions; defined once here so the
  // two can never drift apart.
  /** The row menu's "Annotations": straight to the page, on the version the
   *  row presents, carrying the display name so a rename survives the jump. */
  function openMarkupFor(id: string) {
    const doc = documents.find((d) => d.id === id);
    if (!doc) return;
    navigate(annotationsPath(siteId!, doc.id, doc.version), {
      state: { docName: doc.name, downloadName: downloadNameFor(doc.name, doc.version, doc.approvedVersion) },
    });
  }

  const startNewFolderHere = canCreateInCurrentFolder
    ? () => {
        setSearchInput(''); // clear active search filter so the new row is visible
        return startNewFolder(activeFolderId, 'table');
      }
    : undefined;

  const emptyMessage =
    filesError || isError
      ? t('documents.error')
      : searchQuery
        ? t('documents.emptySearch')
        : activeFolderId === null ? t('documents.selectFolderPrompt') : t('documents.emptyFolder');

  // --- Render-derived data -------------------------------------------------
  // Search filters within the loaded folder's files (on-demand model). Memoised
  // so it only recomputes when the docs or the (debounced) term change, not on
  // every unrelated re-render.
  // Inside a folder being renamed or moved, every file's id is about to change
  // too: the files lock like the folder does.
  const folderSaving = folders.some((f) => f.id === activeFolderId && f.saving);
  const filtered = useMemo(() => {
    const normalized = searchQuery.trim().toLowerCase();
    const shown = folderSaving ? documents.map((doc) => ({ ...doc, saving: true })) : documents;
    if (!normalized) return shown;
    return shown.filter((doc) => doc.name.toLowerCase().includes(normalized));
  }, [documents, searchQuery, folderSaving]);

  // Subfolders of the current folder, shown as rows above the files (roots when
  // "all" is selected). Roots get the same i18n relabel FolderNav applies, and
  // the search box filters them alongside the files.
  const siblingFolders = useMemo(
    () => folders.filter((f) => f.parentId === activeFolderId),
    [folders, activeFolderId],
  );
  const childFolders = useMemo(() => {
    const children = siblingFolders.map((f) => ({ ...f, name: folderDisplayName(t, f) }));
    const normalized = searchQuery.trim().toLowerCase();
    if (!normalized) return children;
    return children.filter((f) => f.name.toLowerCase().includes(normalized));
  }, [siblingFolders, searchQuery, t]);

  // Where the table's "back" row navigates: the selected folder's parent, or
  // the virtual root for top-level folders. The name labels the row ("Back to
  // <name>"). null at the root itself (nothing above it), which hides the row.
  const parentFolder = useMemo(() => {
    if (activeFolderId === null) return null;
    const parentId = folders.find((f) => f.id === activeFolderId)?.parentId ?? null;
    if (parentId === null) return { id: 'all', name: t('documents.allDocuments') };
    const parent = folders.find((f) => f.id === parentId);
    return { id: parentId, name: parent ? folderDisplayName(t, parent) : parentId };
  }, [folders, activeFolderId, t]);

  // Discarding your own draft and pruning a document's history are different
  // acts — nobody has ever seen the draft — so the confirmation says which.
  const discardingOwnDraft = useMemo(() => {
    if (pendingDelete?.type !== 'version') return false;
    const doc = documents.find((d) => d.documentId === pendingDelete.documentId);
    return doc?.versions.find((v) => v.version === pendingDelete.seq)?.status === 'draft';
  }, [documents, pendingDelete]);

  // The document a pending delete targets — its name (and how many versions
  // go with it) belongs in the confirmation.
  const pendingDeleteDoc = useMemo(
    () => (pendingDelete?.type === 'doc' ? documents.find((d) => d.id === pendingDelete.id) : undefined),
    [documents, pendingDelete],
  );

  const selectedDoc = useMemo(
    () => (selectedFileId ? documents.find((d) => d.id === selectedFileId) ?? null : null),
    [documents, selectedFileId],
  );
  // Held through the slide-out, so a closing drawer still has its document.
  const shownDoc = useHeldRecord(selectedDoc);

  // `?doc=` addresses a row by FILE id, and approving a version moves the row
  // onto that version's file — so an upload or markup save that approves itself
  // leaves the open drawer pointing at an id no row carries, and it vanishes.
  // Re-anchor by documentId, READ from the refreshed listing rather than
  // predicted: which version a row presents is the server's call.
  // (Declared after `selectedDoc` on purpose — hoisting it above the memo that
  // produces its input defeats the compiler's memoization check.)
  function followOpenDrawer(folderId: string) {
    // Only ever MOVES an open drawer. A `?doc=` that addresses no row is a
    // stale link with nothing open — following it would open a drawer the user
    // never asked for.
    if (!selectedDoc) return;
    const refreshed = queryClient.getQueryData<IFilesResponse>(['documents', 'files', siteId, folderId]);
    if (!refreshed) return;
    const documentId = selectedDoc.documentId;
    // Without a documentId the row was a standalone file, and this upload just
    // adopted it into a version chain — follow the document that now claims the
    // file the drawer is open on.
    const row = documentId
      ? refreshed.documents.find((d) => d.documentId === documentId)
      : refreshed.documents.find((d) => d.versions.some((v) => v.fileId === selectedFileId));
    if (row && row.id !== selectedFileId) openDoc(row.id);
  }

  // --- Page tour ---------------------------------------------------------
  const tour = useDocumentsTour({
    ready: !isPending && !isError,
    hasFolders: folders.length > 0,
    // A row is listed only inside a folder whose files are in and match the search.
    hasDocumentRow: activeFolderId !== null && !filesPending && !filesError && filtered.length > 0,
    canUpload: canWrite,
    closeUpload,
    closeDrawer,
  });

  // A disconnected/expired provider leaves an honest empty state under the
  // page's header — reconnecting is handled globally by the TopBar (visible
  // here), so no in-page banner.
  if (providerError) {
    return <DataUnavailable />;
  }

  return (
    <>
      {/* No sensors ⇒ nothing is draggable: move stays fully disabled until the
          backend advertises capabilities.move (pending the upstream move API). */}
      <DndContext sensors={canMove && canWrite ? sensors : []} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
        <div className={styles.page}>
          <div className={styles.barRow}>
            <DocumentsBar
              searchQuery={searchInput}
              onSearchChange={setSearchInput}
              onUploadClick={canWrite ? () => setUploadModalOpen(true) : undefined}
              view={view}
              onViewChange={setView}
            />
          </div>

          <FolderNav
            folders={folders}
            selectedId={selectedFolder}
            isPending={isPending}
            isUnavailable={isError}
            onSelect={setSelectedFolder}
            onRenameFolder={canWrite ? renameFolder : undefined}
            onDeleteFolder={(id) => requestDelete({ type: 'folder', id })}
            canDelete={canDelete}
            newFolder={newFolder?.origin === 'tree' ? newFolder : null}
            onStartNewFolder={canWrite ? (parentId) => startNewFolder(parentId, 'tree') : undefined}
            onCommitNewFolder={commitNewFolder}
            onCancelNewFolder={cancelNewFolder}
            invalidDropId={invalidDropId}
          />

          <div className={styles.tableArea} data-tour="documents-pane">
            {isPending ? (
              <div className={styles.stateMessage}>{t('documents.loading')}</div>
            ) : (
              <DocumentView
                view={view}
                isLoading={filesPending}
                siteId={siteId ?? null}
                documents={activeFolderId === null || filesError ? [] : filtered}
                folders={childFolders}
                siblingFolders={siblingFolders}
                parentFolder={parentFolder}
                selectedId={selectedFileId}
                onSelect={(id) => id === null ? closeDrawer() : openDoc(id)}
                onOpenFolder={setSelectedFolder}
                onRenameDoc={canWrite ? renameDoc : undefined}
                onDeleteDoc={(id) => requestDelete({ type: 'doc', id })}
                onUploadNewVersion={canWrite ? openNewVersionUpload : undefined}
                onMarkupDoc={openMarkupFor}
                onRenameFolder={canWrite ? renameFolder : undefined}
                onDeleteFolder={(id) => requestDelete({ type: 'folder', id })}
                canDelete={canDelete}
                // Only in the surface that opened it, and only in its own folder.
                newFolder={newFolder?.origin === 'table' && newFolder.parentId === activeFolderId ? newFolder : null}
                onStartNewFolder={canWrite ? startNewFolderHere : undefined}
                onCommitNewFolder={commitNewFolder}
                onCancelNewFolder={cancelNewFolder}
                emptyMessage={emptyMessage}
                invalidDropId={invalidDropId}
              />
            )}

            {shownDoc && siteId && (
              <DocumentDrawer
                open={selectedDoc !== null}
                doc={shownDoc}
                siteId={siteId}
                compareVersion={compareVersion}
                onCompareVersionChange={(v) => setVersionParam('compare', v)}
                onUploadNewVersion={canWrite ? () => openNewVersionUpload(shownDoc.id) : undefined}
                onDeleteVersion={canWrite ? (v) => shownDoc.documentId && requestDelete({
                  type: 'version', documentId: shownDoc.documentId, seq: v.version,
                }) : undefined}
                onSubmitVersion={canWrite ? (v) => void submitVersion(shownDoc, v.version) : undefined}
                onDecideVersion={(v, decision, comment) => performDecideVersion(shownDoc, v, decision, comment)}
                busyVersionKey={busyVersionKey}
                onClose={closeDrawer}
              />
            )}
          </div>
        </div>
      </DndContext>

      {siteId && uploadModalOpen && (
        <UploadModal
          open={uploadModalOpen}
          siteId={siteId}
          folderId={selectedFolder !== 'all' ? selectedFolder : undefined}
          folderName={selectedFolder !== 'all' ? (folders.find((f) => f.id === selectedFolder)?.name ?? selectedFolder) : undefined}
          folders={folders}
          newVersionTarget={newVersionTarget}
          canApprove={canApprove}
          onClose={closeUpload}
          // Refresh only — the modal closes itself once this resolves, so the
          // table already carries the upload when it disappears.
          onUploaded={async (uploadedFolderId, autoApproveSkipped, renamedTo) => {
            if (autoApproveSkipped) notifyAutoApproveSkipped();
            // Its own name was taken: say what it is called instead.
            if (renamedTo) push({ kind: 'success', title: t('documents.upload.renamedTitle', { name: renamedTo }), sub: t('documents.upload.renamedHint') });
            await queryClient.invalidateQueries({ queryKey: ['documents', 'files', siteId, uploadedFolderId] });
            followOpenDrawer(uploadedFolderId);
          }}
        />
      )}

      <DiscardModal
        open={pendingDelete !== null}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={
          pendingDelete?.type === 'version'
            ? discardingOwnDraft
              ? t('documents.discardDraft.title')
              : t('documents.deleteVersion.title', { version: pendingDelete.seq })
            : pendingDelete?.type === 'folder' ? t('documents.delete.folderTitle') : t('documents.delete.docTitle')
        }
        description={
          pendingDelete?.type === 'version'
            ? discardingOwnDraft ? t('documents.discardDraft.body') : t('documents.deleteVersion.body')
            : pendingDelete?.type === 'folder' ? t('documents.delete.folderBody')
            : !pendingDeleteDoc ? t('documents.delete.docBody')
            : pendingDeleteDoc.versions.length > 1
              ? t('documents.delete.docBodyMany', { name: pendingDeleteDoc.name, count: pendingDeleteDoc.versions.length })
              : t('documents.delete.docBodyOne', { name: pendingDeleteDoc.name })
        }
        confirmLabel={t('documents.delete.confirm')}
        cancelLabel={t('documents.delete.cancel')}
        loading={isDeleting}
      />

      <PageTour
        key={tour.key}
        tourId={DOCUMENTS_TOUR_ID}
        steps={DOCUMENTS_TOUR_STEPS}
        open={tour.open}
        context={tour.context}
        onDismiss={tour.dismiss}
      />
      <Toasts toasts={toasts} onDismiss={dismiss} />
    </>
  );
}